import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Check, Download, FileCog, Loader2, RefreshCw, Send, Wand2, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { eur, fmtDate, invId, isDomTom, statusOf } from "@/lib/invoice";
import { StatusBadge } from "@/components/StatusBadge";

export default function InvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ invoice_number: "", issue_date: "", due_date: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      const doc = data?.invoice || data;
      setInv(doc);
      setForm({
        invoice_number: doc.invoice_number || "",
        issue_date: (doc.issue_date || "").slice(0, 10),
        due_date: (doc.due_date || "").slice(0, 10),
      });
    } catch {
      toast.error("Facture introuvable");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    } finally {
      setBusy("");
    }
  };

  const downloadFile = async (path) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = path.split("/").pop() || "fichier";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const saveEdit = () =>
    act("save", () => api.put(`/invoices/${id}`, {
      invoice_number: form.invoice_number || null,
      issue_date: form.issue_date || null,
      due_date: form.due_date || null,
    }), "Facture mise à jour").then(() => setEditing(false));

  if (loading) {
    return (
      <div className="flex justify-center py-32" data-testid="invoice-detail-loading">
        <div className="h-10 w-10 rounded-full border-2 border-[#2563EB] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!inv) return null;

  const received = inv.direction === "received";
  const files = [inv.facturx_file, inv.facturx_path, inv.pdf_file, inv.pdf_path, inv.file_path].filter(Boolean);
  const lines = inv.lines || [];

  return (
    <div data-testid="invoice-detail-page" className="max-w-5xl">
      <Link to="/app/factures" data-testid="back-to-invoices" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-200 mb-6">
        <ArrowLeft className="h-4 w-4" /> Retour aux factures
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <p className="overline-label mb-2">{received ? "Facture reçue" : "Facture émise"}</p>
          <h1 className="font-display font-light tracking-tighter text-3xl md:text-4xl font-mono" data-testid="invoice-number">
            {inv.invoice_number || invId(inv)}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <StatusBadge invoice={inv} />
            {isDomTom(inv) && (
              <span data-testid="octroi-badge" className="inline-flex items-center rounded-full border border-[#00D4FF]/30 bg-[#00D4FF]/10 px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wider text-[#00D4FF]">
                Octroi de mer {inv.region ? `· ${inv.region}` : ""}
              </span>
            )}
            {inv.vat_applicable === false && (
              <span className="inline-flex items-center rounded-full border border-white/15 px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400">
                TVA non applicable
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setEditing(!editing)}
          data-testid="edit-toggle-button"
          className="rounded-full border border-white/20 px-5 py-2.5 text-sm hover:border-[#00D4FF] transition-colors duration-200"
        >
          {editing ? "Annuler" : "Modifier"}
        </button>
      </div>

      {editing && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-[#2563EB]/40 bg-[#0A0E17] p-6 mb-8" data-testid="edit-panel">
          <div className="grid sm:grid-cols-3 gap-4">
            {[["Numéro", "invoice_number", "text"], ["Date d'émission", "issue_date", "date"], ["Échéance", "due_date", "date"]].map(([label, key, type]) => (
              <div key={key}>
                <label className="overline-label block mb-2">{label}</label>
                <input
                  type={type}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  data-testid={`edit-${key}`}
                  className="w-full rounded-xl bg-black/40 border border-white/10 px-4 py-2.5 text-sm focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] outline-none"
                />
              </div>
            ))}
          </div>
          <button
            onClick={saveEdit}
            disabled={busy === "save"}
            data-testid="save-edit-button"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold hover:bg-[#00D4FF] hover:text-[#050505] transition-colors duration-200 disabled:opacity-50"
          >
            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Enregistrer
          </button>
        </motion.div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-10" data-testid="action-bar">
        <ActionBtn testid="autocorrect-button" busy={busy === "autocorrect"} onClick={() => act("autocorrect", () => api.post(`/invoices/${id}/autocorrect`), "Autocorrection IA appliquée")} icon={<Wand2 className="h-4 w-4" />} label="Autocorriger (IA)" />
        <ActionBtn testid="convert-button" busy={busy === "convert"} onClick={() => act("convert", () => api.post(`/invoices/${id}/convert`, { format: "facturx", autocorrect: true }), "Conversion Factur-X terminée")} icon={<FileCog className="h-4 w-4" />} label="Convertir Factur-X" primary />
        {!received && (
          <ActionBtn testid="send-pdp-button" busy={busy === "send"} onClick={() => act("send", () => api.post(`/invoices/${id}/send`), "Facture transmise à la PDP")} icon={<Send className="h-4 w-4" />} label="Envoyer via PDP" />
        )}
        <ActionBtn testid="refresh-pdp-button" busy={busy === "refresh"} onClick={() => act("refresh", () => api.post(`/invoices/${id}/pdp/refresh`), "Statut PDP synchronisé")} icon={<RefreshCw className="h-4 w-4" />} label="Rafraîchir statut" />
        {received && ["received", "sent", "validated"].includes(statusOf(inv)) && (
          <>
            <ActionBtn testid="accept-button" busy={busy === "accept"} onClick={() => act("accept", () => api.post(`/invoices/${id}/pdp/decision`, { action: "accept" }), "Facture acceptée")} icon={<Check className="h-4 w-4" />} label="Accepter" />
            <ActionBtn testid="reject-button" busy={busy === "reject"} danger onClick={() => act("reject", () => api.post(`/invoices/${id}/pdp/decision`, { action: "reject" }), "Facture refusée")} icon={<X className="h-4 w-4" />} label="Refuser" />
          </>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {[["Vendeur", inv.seller], ["Acheteur", inv.buyer]].map(([label, party]) => (
          <div key={label} className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid={`party-${label.toLowerCase()}`}>
            <p className="overline-label mb-3">{label}</p>
            <p className="font-medium">{party?.name || "—"}</p>
            {party?.siret && <p className="font-mono text-xs text-zinc-500 mt-1">SIRET {party.siret}</p>}
            {party?.vat_number && <p className="font-mono text-xs text-zinc-500">TVA {party.vat_number}</p>}
            {party?.address && <p className="text-xs text-zinc-400 mt-2 whitespace-pre-line">{party.address}</p>}
          </div>
        ))}
      </div>

      {lines.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-[#0A0E17] overflow-hidden mb-8" data-testid="lines-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="px-5 py-3.5 overline-label">Description</th>
                  <th className="px-5 py-3.5 overline-label text-right">Qté</th>
                  <th className="px-5 py-3.5 overline-label text-right">PU HT</th>
                  <th className="px-5 py-3.5 overline-label text-right">TVA</th>
                  <th className="px-5 py-3.5 overline-label text-right">Octroi</th>
                  <th className="px-5 py-3.5 overline-label text-right">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-white/5 last:border-0">
                    <td className="px-5 py-3.5 text-zinc-300 max-w-[260px] truncate">{l.description}</td>
                    <td className="px-5 py-3.5 text-right font-mono text-zinc-400">{l.quantity}</td>
                    <td className="px-5 py-3.5 text-right font-mono text-zinc-400">{eur(l.unit_price)}</td>
                    <td className="px-5 py-3.5 text-right font-mono text-zinc-400">{l.vat_rate}%</td>
                    <td className="px-5 py-3.5 text-right font-mono">
                      {Number(l.octroi_amount) > 0 ? <span className="text-[#00D4FF]">{eur(l.octroi_amount)}</span> : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-5 py-3.5 text-right font-mono">{eur(l.total_ht)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid="totals-panel">
          <p className="overline-label mb-4">Totaux</p>
          <dl className="space-y-2.5 text-sm">
            <Row label="Total HT" value={eur(inv.total_ht)} />
            <Row label="TVA" value={eur(inv.total_tva)} />
            {isDomTom(inv) && <Row label="Octroi de mer" value={eur(inv.total_octroi)} accent />}
            <Row label={`Total TTC ${inv.currency ? `(${inv.currency})` : ""}`} value={eur(inv.total_ttc)} bold />
          </dl>
          <p className="mt-4 text-xs text-zinc-500">
            Émise le {fmtDate(inv.issue_date)} · Échéance {fmtDate(inv.due_date)}
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6" data-testid="files-panel">
          <p className="overline-label mb-4">Fichiers</p>
          {files.length === 0 ? (
            <p className="text-sm text-zinc-500">Aucun fichier généré pour le moment. Lancez une conversion Factur-X.</p>
          ) : (
            <ul className="space-y-2">
              {files.map((f) => (
                <li key={f}>
                  <button
                    onClick={() => downloadFile(f)}
                    data-testid={`download-${f.split("/").pop()}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-mono hover:border-[#00D4FF] hover:text-[#00D4FF] transition-colors duration-200"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {f.split("/").pop()}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, accent, bold }) => (
  <div className="flex justify-between items-baseline">
    <dt className="text-zinc-400">{label}</dt>
    <dd className={`font-mono ${bold ? "text-lg font-semibold text-white" : ""} ${accent ? "text-[#00D4FF]" : ""}`}>{value}</dd>
  </div>
);

const ActionBtn = ({ testid, busy, onClick, icon, label, primary, danger }) => (
  <button
    onClick={onClick}
    disabled={busy}
    data-testid={testid}
    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-[transform,background-color,color,border-color] duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 ${
      primary
        ? "bg-[#2563EB] text-white hover:bg-[#00D4FF] hover:text-[#050505]"
        : danger
          ? "border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
          : "border border-white/20 text-zinc-200 hover:border-[#00D4FF] hover:text-white"
    }`}
  >
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
    {label}
  </button>
);
